import React from "react";
import { RefreshCw, PlayCircle, Loader2, AlertCircle } from "lucide-react";
import { useQueueHealth } from "./queue_health/useQueueHealth";
import { fmt, LiveBadge, Cell, SiteRow, SavingsFooter } from "./queue_health/QueueHealthBits";

/** Queue Health widget — per-site pipeline counts (live via SSE) + one-click sweeper trigger. */
export default function QueueHealthWidget() {
  const { data, err, loading, sweeping, sweepResult, liveState, savings, fetchHealth, runSweepers } = useQueueHealth();

  return (
    <div data-testid="queue-health-widget" className="border border-zinc-800 bg-zinc-900/40 rounded-sm">
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
        <div>
          <div className="label-tiny flex items-center gap-2">Queue Health <LiveBadge state={liveState} /></div>
          <div className="text-[11px] text-zinc-500 font-mono mt-0.5">Article pipeline + freshness sweepers</div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="queue-health-refresh-button" onClick={fetchHealth} disabled={loading} className="p-1.5 text-zinc-500 hover:text-zinc-200 disabled:opacity-40 transition-colors" title="Refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            data-testid="queue-health-run-sweepers-button"
            onClick={runSweepers}
            disabled={sweeping}
            className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 px-2.5 py-1 rounded-sm transition-colors disabled:opacity-40"
          >
            {sweeping ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />}
            {sweeping ? "Running…" : "Run sweepers"}
          </button>
        </div>
      </div>

      {err && (
        <div data-testid="queue-health-error" className="px-5 py-3 text-xs text-red-400 font-mono border-b border-zinc-800 bg-red-950/20 flex items-center gap-2">
          <AlertCircle size={12} /> {err}
        </div>
      )}

      {sweepResult && (
        <div data-testid="queue-health-sweep-result" className="px-5 py-2.5 text-xs font-mono border-b border-zinc-800 bg-emerald-950/20 text-emerald-400 flex items-center gap-3">
          <span>✓ Sweep complete</span>
          <span className="text-zinc-500">·</span>
          <span>{fmt(sweepResult.deleted)} pending deleted</span>
          <span className="text-zinc-500">·</span>
          <span>{fmt(sweepResult.archived)} ready archived</span>
        </div>
      )}

      {data && (
        <div className="grid grid-cols-4 gap-px bg-zinc-800/50 border-b border-zinc-800">
          <Cell label="Pending" value={fmt(data.totals.pending)} testid="qh-total-pending" hint={`${fmt(data.totals.would_purge_pending)} stale`} />
          <Cell label="Ready" value={fmt(data.totals.ready)} testid="qh-total-ready" hint={`${fmt(data.totals.would_archive_ready)} aged`} />
          <Cell label="Published" value={fmt(data.totals.published)} testid="qh-total-published" accent="#10b981" />
          <Cell label="Archived" value={fmt(data.totals.archived)} testid="qh-total-archived" />
        </div>
      )}

      <div className="divide-y divide-zinc-800">
        {loading && !data && <div className="px-5 py-6 text-sm text-zinc-500 font-mono">Loading…</div>}
        {data && data.sites.length === 0 && <div className="px-5 py-6 text-sm text-zinc-500 font-mono">No sites yet.</div>}
        {data && data.sites.map((s) => <SiteRow key={s.site_id} site={s} />)}
      </div>

      {data && <SavingsFooter data={data} savings={savings} />}
    </div>
  );
}
